/**
 * E-mails sortants proposés depuis les files « À relancer » (phase 2.1,
 * GO Dove 2026-08-10).
 *
 * Un e-mail n'est JAMAIS envoyé au moment où on le propose : il naît en
 * brouillon, et c'est le réglage de validation de son type de décision
 * (`reglage_validation`, défaut « proposer ») qui décide s'il attend une
 * validation humaine. Le modèle est un POINT DE DÉPART sobre : le validateur
 * relit, corrige, puis envoie — jamais l'inverse.
 */
import { z } from "zod";
import type { ActionExplanation, Queue } from "./relances";
import { QUEUES } from "./relances";

/**
 * Les files E-MAIL : les trois files de relances, plus PARTAGE (lot G).
 * PARTAGE n'est PAS une file de relances — elle n'existe que pour classer
 * les brouillons ; `QUEUES` (relances.ts) reste à trois et ne bouge pas.
 */
export const QUEUES_EMAIL = [...QUEUES, "PARTAGE"] as const;
export type QueueEmail = (typeof QUEUES_EMAIL)[number];

/** File → type de décision (les trois types sortants existent depuis le 2026-08-05). */
export const TYPE_DECISION_PAR_QUEUE: Record<QueueEmail, string> = {
  RELATION: "relance_relation",
  WORK: "relance_retard",
  FINANCE: "relance_financiere",
  PARTAGE: "notification_partage",
};

export const STATUTS_BROUILLON = ["propose", "envoye", "refuse"] as const;
export type StatutBrouillon = (typeof STATUTS_BROUILLON)[number];

export const STATUT_BROUILLON_LABELS: Record<StatutBrouillon, string> = {
  propose: "Proposé — en attente de validation",
  envoye: "Envoyé",
  refuse: "Refusé",
};

/** Forme minimale d'une adresse : on vérifie la FORME, pas l'existence. */
export function estAdresseEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export const BrouillonEmailSchema = z.object({
  id: z.string().uuid(),
  actionCaseId: z.string().uuid().nullable(),
  partageCasId: z.string().uuid().nullable(),
  queue: z.enum(QUEUES_EMAIL),
  typeDecision: z.string(),
  cabinetNom: z.string(),
  /** Boîte expéditrice pressentie — null si aucune boîte connectée. */
  boite: z.string().nullable(),
  /** Destinataire résolu (dernier e-mail entrant du cabinet, puis Vevidental) — null si inconnu. */
  destinataire: z.string().nullable(),
  sujet: z.string(),
  corps: z.string(),
  statut: z.enum(STATUTS_BROUILLON),
  /** Dernière erreur d'envoi (le brouillon reste proposé, corrigeable). */
  erreur: z.string().nullable(),
  motifRefus: z.string().nullable(),
  creePar: z.string(),
  creeAt: z.string(),
  decidePar: z.string().nullable(),
  envoyeAt: z.string().nullable(),
});
export type BrouillonEmail = z.infer<typeof BrouillonEmailSchema>;

export const BrouillonsResponseSchema = z.object({
  items: z.array(BrouillonEmailSchema),
});
export type BrouillonsResponse = z.infer<typeof BrouillonsResponseSchema>;

/* ---------------------------------------------------------------- modèles */

function dateFr(iso: string | null): string {
  if (!iso) return "";
  const [a, m, j] = iso.split("-");
  return a && m && j ? `${j}/${m}/${a}` : iso;
}

function salutation(practitionerNom: string | null): string {
  return practitionerNom ? `Bonjour Docteur ${practitionerNom},` : "Bonjour,";
}

const SIGNATURE = "Bien cordialement,\nLaboratoire GéCéram";

export interface EntreeModele {
  queue: Queue;
  cabinetNom: string;
  practitionerNom: string | null;
  explanation: ActionExplanation;
}

/**
 * Le modèle par file. Ton sobre, sans métrique interne (un praticien n'a pas à
 * lire « votre cadence habituelle ») et sans promesse — tout est relisible et
 * modifiable avant envoi.
 */
export function modeleEmail(e: EntreeModele): { sujet: string; corps: string } | null {
  const expl = e.explanation;
  if (expl.ruleVersion === "relation-inactivity/v1") {
    return {
      sujet: "Laboratoire GéCéram — prise de nouvelles",
      corps: [
        salutation(e.practitionerNom),
        "",
        `Nous n'avons pas reçu de nouveau travail de votre cabinet depuis le ${dateFr(expl.dernierTravailLe)}.`,
        "Nous tenions à prendre de vos nouvelles et à nous assurer que tout se passe bien de votre côté.",
        "",
        "Si un dossier est en préparation ou si quelque chose a changé dans votre organisation,",
        "n'hésitez pas à nous en faire part — nous restons à votre disposition.",
        "",
        SIGNATURE,
      ].join("\n"),
    };
  }
  if (expl.ruleVersion === "work-alerts/v1") {
    const echeance = expl.echeance ? ` (échéance du ${dateFr(expl.echeance)})` : "";
    const detail = expl.detail ? `\nÉlément concerné : ${expl.detail}.` : "";
    return {
      sujet: `Travail ${expl.workCode} — point d'avancement`,
      corps: [
        salutation(e.practitionerNom),
        "",
        `Nous faisons le point sur le travail ${expl.workCode}${echeance}.${detail}`,
        "Si un élément de votre côté est nécessaire pour le finaliser, merci de nous le transmettre",
        "afin que nous puissions tenir le délai prévu.",
        "",
        SIGNATURE,
      ].join("\n"),
    };
  }
  if (expl.ruleVersion === "ressaisie-vevidental/v1") {
    // La ressaisie Vevidental est une tâche INTERNE (Élodie) : écrire au
    // cabinet à son sujet n'aurait aucun sens. La route rend ce refus en 400.
    return null;
  }
  if (expl.ruleVersion === "echange-resume/v1") {
    // Une action née d'un résumé IA (L2) répond à un échange précis : la
    // réponse s'écrit à la main, pas depuis un modèle générique.
    return null;
  }
  const montant =
    expl.resteDu !== null ? ` d'un montant restant dû de ${expl.resteDu.toFixed(2).replace(".", ",")} €` : "";
  return {
    sujet: `Facture ${expl.numeroFacture} — rappel de règlement`,
    corps: [
      salutation(e.practitionerNom),
      "",
      `Sauf erreur de notre part, la facture ${expl.numeroFacture}${montant} demeure en attente de règlement.`,
      "Si celui-ci a été effectué récemment, merci de ne pas tenir compte de ce message.",
      "Dans le cas contraire, nous vous remercions de bien vouloir le régulariser, ou de nous",
      "contacter pour convenir ensemble d'une solution.",
      "",
      SIGNATURE,
    ].join("\n"),
  };
}
